export type ActivityTimelineCadenceId =
    | 'day'
    | 'multi-day'
    | 'week'
    | 'multi-week'
    | 'month'
    | 'quarter'
    | 'multi-month'
    | 'multi-quarter'
    | 'year'
    | 'multi-year'
    | 'all-time';

export interface ActivityTimelineCadence {
    bucketMilliseconds: number;
    id: ActivityTimelineCadenceId;
    label: string;
    minimumPlan: 'enterprise' | 'plus';
    spanMilliseconds: number;
}

export interface WorkspaceActivityTimelineActor {
    avatar?: string;
    id: string;
    name: string;
}

export interface WorkspaceActivityTimelineEvent {
    action: string;
    actor?: WorkspaceActivityTimelineActor;
    eventType: string;
    id: string;
    resourceId: string;
    resourceName: string;
    resourceType: string;
    timestamp: string;
}

export interface WorkspaceActivityTimelineCluster {
    bucketEnd: Date;
    bucketStart: Date;
    events: WorkspaceActivityTimelineEvent[];
    id: string;
    timestamp: Date;
}

export interface WorkspaceActivityTimelineResource {
    id: string;
    name: string;
    type: string;
}

export interface WorkspaceActivityTimelineParticipants {
    actors: WorkspaceActivityTimelineActor[];
    resources: WorkspaceActivityTimelineResource[];
}

export type ActivityTimelineTickUnit = 'day' | 'hour' | 'minute' | 'month' | 'quarter' | 'week' | 'year';

export interface ActivityTimelineTickHierarchy {
    fine: { step: number; unit: ActivityTimelineTickUnit };
    fineRevealZoom: number;
    major: { step: number; unit: ActivityTimelineTickUnit };
    minor: { step: number; unit: ActivityTimelineTickUnit };
}

const HOUR = 60 * 60 * 1000;
const DAY = 24 * HOUR;

export const ACTIVITY_TIMELINE_CADENCES: ActivityTimelineCadence[] = [
    { id: 'day', label: 'Day', spanMilliseconds: DAY, bucketMilliseconds: HOUR, minimumPlan: 'plus' },
    { id: 'multi-day', label: '3 Days', spanMilliseconds: 3 * DAY, bucketMilliseconds: 3 * HOUR, minimumPlan: 'plus' },
    { id: 'week', label: 'Week', spanMilliseconds: 7 * DAY, bucketMilliseconds: 12 * HOUR, minimumPlan: 'plus' },
    { id: 'multi-week', label: '3 Weeks', spanMilliseconds: 21 * DAY, bucketMilliseconds: DAY, minimumPlan: 'plus' },
    { id: 'month', label: 'Month', spanMilliseconds: 30 * DAY, bucketMilliseconds: DAY, minimumPlan: 'plus' },
    { id: 'quarter', label: 'Quarter', spanMilliseconds: 91 * DAY, bucketMilliseconds: 7 * DAY, minimumPlan: 'plus' },
    {
        id: 'multi-month',
        label: '6 Months',
        spanMilliseconds: 182 * DAY,
        bucketMilliseconds: 14 * DAY,
        minimumPlan: 'plus',
    },
    {
        id: 'multi-quarter',
        label: '3 Quarters',
        spanMilliseconds: 274 * DAY,
        bucketMilliseconds: 30 * DAY,
        minimumPlan: 'plus',
    },
    { id: 'year', label: 'Year', spanMilliseconds: 365 * DAY, bucketMilliseconds: 30 * DAY, minimumPlan: 'plus' },
    {
        id: 'multi-year',
        label: '3 Years',
        spanMilliseconds: 3 * 365 * DAY,
        bucketMilliseconds: 91 * DAY,
        minimumPlan: 'enterprise',
    },
    {
        id: 'all-time',
        label: 'All Time',
        spanMilliseconds: 100 * 365 * DAY,
        bucketMilliseconds: 365 * DAY,
        minimumPlan: 'enterprise',
    },
];

export const getActivityTimelineCadence = (id: ActivityTimelineCadenceId): ActivityTimelineCadence =>
    ACTIVITY_TIMELINE_CADENCES.find((cadence) => cadence.id === id) ?? ACTIVITY_TIMELINE_CADENCES[2];

export const getActivityTimelineParticipants = (
    events: WorkspaceActivityTimelineEvent[],
): WorkspaceActivityTimelineParticipants => {
    const actors = new Map<string, WorkspaceActivityTimelineActor>();
    const resources = new Map<string, WorkspaceActivityTimelineResource>();

    events.forEach((event) => {
        const actor = event.actor ?? { id: 'workspace-activity', name: 'Workspace activity' };
        const actorKey = actor.id || actor.name;
        const resourceKey = `${event.resourceType}:${event.resourceId}`;

        if (!actors.has(actorKey)) actors.set(actorKey, actor);
        if (!resources.has(resourceKey)) {
            resources.set(resourceKey, {
                id: event.resourceId,
                name: event.resourceName,
                type: event.resourceType,
            });
        }
    });

    return { actors: Array.from(actors.values()), resources: Array.from(resources.values()) };
};

/**
 * Semantic tick levels keep the timeline legible at its default scale while
 * progressively revealing finer context during trackpad zoom. The day view,
 * for example, always anchors on day dashes, shows hours as dots, and reveals
 * minute dots as the user zooms in.
 */
export const getActivityTimelineTickHierarchy = (id: ActivityTimelineCadenceId): ActivityTimelineTickHierarchy => {
    switch (id) {
        case 'day':
            return {
                fine: { step: 1, unit: 'minute' },
                fineRevealZoom: 2,
                major: { step: 1, unit: 'day' },
                minor: { step: 1, unit: 'hour' },
            };
        case 'multi-day':
            return {
                fine: { step: 1, unit: 'hour' },
                fineRevealZoom: 2.5,
                major: { step: 1, unit: 'day' },
                minor: { step: 6, unit: 'hour' },
            };
        case 'week':
            return {
                fine: { step: 1, unit: 'hour' },
                fineRevealZoom: 3,
                major: { step: 1, unit: 'day' },
                minor: { step: 6, unit: 'hour' },
            };
        case 'multi-week':
        case 'month':
            return {
                fine: { step: 6, unit: 'hour' },
                fineRevealZoom: 3,
                major: { step: 1, unit: 'week' },
                minor: { step: 1, unit: 'day' },
            };
        case 'quarter':
        case 'multi-month':
            return {
                fine: { step: 1, unit: 'day' },
                fineRevealZoom: 3.5,
                major: { step: 1, unit: 'month' },
                minor: { step: 1, unit: 'week' },
            };
        case 'multi-quarter':
        case 'year':
            return {
                fine: { step: 1, unit: 'week' },
                fineRevealZoom: 4,
                major: { step: 1, unit: 'quarter' },
                minor: { step: 1, unit: 'month' },
            };
        case 'multi-year':
        case 'all-time':
            return {
                fine: { step: 1, unit: 'month' },
                fineRevealZoom: 4,
                major: { step: 1, unit: 'year' },
                minor: { step: 1, unit: 'quarter' },
            };
    }
};

export const getActivityTimelineWindow = (
    cadenceId: ActivityTimelineCadenceId,
    anchor: Date = new Date(),
    allTimeStart?: Date | string,
): { end: Date; start: Date } => {
    const cadence = getActivityTimelineCadence(cadenceId);
    const end = new Date(anchor);
    const requestedStart = allTimeStart ? new Date(allTimeStart) : undefined;

    if (
        cadenceId === 'all-time' &&
        requestedStart &&
        Number.isFinite(requestedStart.getTime()) &&
        requestedStart <= end
    ) {
        return { end, start: requestedStart };
    }

    return { end, start: new Date(end.getTime() - cadence.spanMilliseconds) };
};

export const aggregateActivityTimelineEvents = (
    events: WorkspaceActivityTimelineEvent[],
    cadenceId: ActivityTimelineCadenceId,
    anchor: Date = new Date(),
    allTimeStart?: Date | string,
): WorkspaceActivityTimelineCluster[] => {
    const cadence = getActivityTimelineCadence(cadenceId);
    const { end, start } = getActivityTimelineWindow(cadenceId, anchor, allTimeStart);
    const clusters = new Map<number, WorkspaceActivityTimelineEvent[]>();

    events.forEach((event) => {
        const timestamp = new Date(event.timestamp).getTime();

        if (!Number.isFinite(timestamp) || timestamp < start.getTime() || timestamp > end.getTime()) {
            return;
        }

        const bucket = Math.floor((timestamp - start.getTime()) / cadence.bucketMilliseconds);
        clusters.set(bucket, [...(clusters.get(bucket) ?? []), event]);
    });

    return Array.from(clusters.entries())
        .sort(([left], [right]) => left - right)
        .map(([bucket, bucketEvents]) => {
            const bucketStart = new Date(start.getTime() + bucket * cadence.bucketMilliseconds);
            const bucketEnd = new Date(Math.min(bucketStart.getTime() + cadence.bucketMilliseconds, end.getTime()));
            const averageTimestamp =
                bucketEvents.reduce((total, event) => total + new Date(event.timestamp).getTime(), 0) /
                bucketEvents.length;

            return {
                bucketEnd,
                bucketStart,
                events: bucketEvents.sort(
                    (left, right) => new Date(right.timestamp).getTime() - new Date(left.timestamp).getTime(),
                ),
                id: `${cadenceId}-${bucketStart.toISOString()}`,
                timestamp: new Date(averageTimestamp),
            };
        });
};

export const stepActivityTimelineCadence = (
    current: ActivityTimelineCadenceId,
    direction: -1 | 1,
    available: ActivityTimelineCadenceId[],
): ActivityTimelineCadenceId => {
    const ordered = ACTIVITY_TIMELINE_CADENCES.map(({ id }) => id).filter((id) => available.includes(id));
    const currentIndex = Math.max(ordered.indexOf(current), 0);
    const nextIndex = Math.min(Math.max(currentIndex + direction, 0), ordered.length - 1);

    return ordered[nextIndex] ?? current;
};
